import { formatBackendDateOnly } from "utils/common";

import { stripAnvilParityMergeTags } from "./merge-tags-options/anvil-parity-merge-tags.utils";
import { APPOINTMENT_PROVIDER_MENU_OPTIONS } from "./merge-tags-options/appointment-provider.utils";
import { CLINIC_MENU_OPTIONS } from "./merge-tags-options/clinic.utils";
import { stripClinicalMergeTags } from "./merge-tags-options/clinical-merge-tags.utils";
import { CURRENT_USER_MENU_OPTIONS } from "./merge-tags-options/current-user.utils";
import { MISCELLANEOUS_MENU_OPTIONS } from "./merge-tags-options/miscellaneous.utils";
import { PATIENT_ACTIVE_PRODUCTS_MENU_OPTIONS } from "./merge-tags-options/patient-active-products.utils";
import {
  PATIENT_APPOINTMENTS_MENU_OPTIONS,
  PATIENT_CURRENT_APPOINTMENT_MENU_OPTIONS
} from "./merge-tags-options/patient-appointments.utils";
import { PATIENT_BALANCE_MENU_OPTIONS } from "./merge-tags-options/patient-balance-utils";
import { PATIENT_CURRENT_REPORT_MENU_OPTIONS } from "./merge-tags-options/patient-current-report.utils";
import {
  PATIENT_INFO_MENU_OPTIONS,
  PATIENT_INFO_MENU_OPTIONS_WITH_PRONOUNS
} from "./merge-tags-options/patient-info";
import { PATIENT_LAST_SIGNED_REPORT_MENU_OPTIONS } from "./merge-tags-options/patient-last-report.utils";
import { PATIENT_MEDICARE_INSURANCE_POLICY_MENU_OPTIONS } from "./merge-tags-options/patient-medicare-insurance-policy.utils";
import { PATIENT_PRIMARY_INSURANCE_POLICY_MENU_OPTIONS } from "./merge-tags-options/patient-primary-insurance-policy.utils";
import { PATIENT_SECONDARY_INSURANCE_POLICY_MENU_OPTIONS } from "./merge-tags-options/patient-secondary-insurance-policy.utils";
import { REPLACE_TAGS_MENU_OPTIONS } from "./merge-tags-options/replace-tags.utils";
import {
  MERGE_TAGS_VALUE_HANDLERS,
  type MergeTagOptions,
  type MergeTagsReplaceHandlerParams
} from "./merge-tags-values/merge-tags-values";
import { isWildcardTag } from "./replace-wildcards.utils";

export const MERGE_TAGS_OPTIONS = [
  PATIENT_INFO_MENU_OPTIONS,
  PATIENT_APPOINTMENTS_MENU_OPTIONS,
  PATIENT_PRIMARY_INSURANCE_POLICY_MENU_OPTIONS,
  PATIENT_SECONDARY_INSURANCE_POLICY_MENU_OPTIONS,
  PATIENT_LAST_SIGNED_REPORT_MENU_OPTIONS,
  PATIENT_CURRENT_REPORT_MENU_OPTIONS,
  PATIENT_ACTIVE_PRODUCTS_MENU_OPTIONS,
  PATIENT_BALANCE_MENU_OPTIONS,
  CURRENT_USER_MENU_OPTIONS,
  APPOINTMENT_PROVIDER_MENU_OPTIONS,
  CLINIC_MENU_OPTIONS,
  REPLACE_TAGS_MENU_OPTIONS,
  MISCELLANEOUS_MENU_OPTIONS
];

export const MERGE_TAGS_OPTIONS_WITH_PRONOUNS = [
  PATIENT_INFO_MENU_OPTIONS_WITH_PRONOUNS,
  PATIENT_APPOINTMENTS_MENU_OPTIONS,
  PATIENT_PRIMARY_INSURANCE_POLICY_MENU_OPTIONS,
  PATIENT_SECONDARY_INSURANCE_POLICY_MENU_OPTIONS,
  PATIENT_LAST_SIGNED_REPORT_MENU_OPTIONS,
  PATIENT_CURRENT_REPORT_MENU_OPTIONS,
  PATIENT_ACTIVE_PRODUCTS_MENU_OPTIONS,
  PATIENT_BALANCE_MENU_OPTIONS,
  CURRENT_USER_MENU_OPTIONS,
  APPOINTMENT_PROVIDER_MENU_OPTIONS,
  CLINIC_MENU_OPTIONS,
  REPLACE_TAGS_MENU_OPTIONS,
  MISCELLANEOUS_MENU_OPTIONS
];

export const buildMergeTagsOptions = (
  isCurrentAppointmentEnabled: boolean,
  isClinicalMergeTagsEnabled = false,
  baseOptions = MERGE_TAGS_OPTIONS,
  isAnvilParityEnabled = false
) => {
  const options = isClinicalMergeTagsEnabled
    ? baseOptions
    : baseOptions.map((group) =>
        group === PATIENT_CURRENT_REPORT_MENU_OPTIONS ||
        group === PATIENT_LAST_SIGNED_REPORT_MENU_OPTIONS
          ? stripClinicalMergeTags(group)
          : group
      );

  const withCurrentAppointment = !isCurrentAppointmentEnabled
    ? options
    : options.map((group) => {
        if (group === PATIENT_APPOINTMENTS_MENU_OPTIONS) {
          return {
            ...group,
            menu: [PATIENT_CURRENT_APPOINTMENT_MENU_OPTIONS, ...group.menu]
          };
        }
        return group;
      });

  if (!isAnvilParityEnabled) {
    return withCurrentAppointment.map(stripAnvilParityMergeTags);
  }

  // The Medicare group sits after the secondary policy (SN-19877).
  return withCurrentAppointment.flatMap((group) =>
    group === PATIENT_SECONDARY_INSURANCE_POLICY_MENU_OPTIONS
      ? [group, PATIENT_MEDICARE_INSURANCE_POLICY_MENU_OPTIONS]
      : [group]
  );
};

// Nikolay: Without this hack TinyMCE changes table html content
// while inserting it inside <p> tag. We are forcibly closing <p> tag
// here to avoid table insertion inside <p></p>.
// TinyMCE will find unclosed p tags and fix them automatically.
export const allowTableInsidePTag = (value: string) => {
  return `</p>${value}<p></p>`; //TinyMCE will automatically transform this to <p>&nbsp;</p>${value}<p>&nbsp;</p>
};

export type BuildMergeTagValueParams = {
  name: string;
  defaultOptions?: Record<string, any>;
} & MergeTagsReplaceHandlerParams;

// Nikolay: Merge Tag options example:
// {{Patient.SelectedInvoice.Test(total_amount=yes,total_patient_payments=yes)}}
export const parseMergeTagOptions = (
  name: string,
  defaultOptions?: Record<string, any> | undefined
) => {
  let options: MergeTagOptions = {};

  let mergeTagName = name;

  if (name.startsWith("{{") && name.includes("(")) {
    const afterOpen = name.substring(2);
    const beforeParen = afterOpen.split("(")[0];
    mergeTagName = beforeParen;
  }

  if (name.endsWith("}}") && name.includes("(")) {
    const openParenIndex = name.lastIndexOf("(");
    const nameWithoutOptions = name.substring(0, openParenIndex);
    let optionsString = name.substring(openParenIndex + 1, name.length - 3);

    if (optionsString.endsWith("(")) {
      optionsString = optionsString.slice(0, -1);
    }
    options = optionsString.split(",").reduce((acc, option) => {
      const [key, value] = option.split("=");
      let computedValue: any = value;
      if (value === "yes") {
        computedValue = true;
      } else if (value === "no") {
        computedValue = false;
      }
      acc[key] = computedValue;
      return acc;
    }, {} as MergeTagOptions);

    mergeTagName = nameWithoutOptions + "}}";
  } else if (defaultOptions) {
    options = defaultOptions;
  }

  return { options, mergeTagName };
};

export const buildMergeTagOptionsString = (
  options: MergeTagOptions | undefined
) => {
  if (!options || !Object.values(options).length) return "";

  const optionsString = Object.entries(options)
    .filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== "" &&
        value !== "no"
    )
    .map(([key, value]) => {
      let computedValue = value;

      if (value === true || value === "true") {
        computedValue = "yes";
      } else if (value === false || value === "false") {
        computedValue = "no";
      }

      return `${key}=${computedValue}`;
    })
    .join(",");

  return `(${optionsString})`;
};

export const CURRENT_REPORT_TAG_PREFIX = "{{Patient.CurrentReport.";
export const CURRENT_APPOINTMENT_TAG_PREFIX = "{{Patient.CurrentAppointment.";

export const buildMergeTagValue = (params: BuildMergeTagValueParams) => {
  const { name, defaultOptions, ...handlerParams } = params;

  // Replace wildcards are reserved and never resolved against data — return the
  // literal token so it persists for highlighting / manual fill-in.
  if (isWildcardTag(name)) {
    return name;
  }

  const { options, mergeTagName } = parseMergeTagOptions(name, defaultOptions);

  if (
    mergeTagName.startsWith(CURRENT_REPORT_TAG_PREFIX) &&
    !handlerParams.data.currentReport
  ) {
    return mergeTagName;
  }

  if (
    mergeTagName.startsWith(CURRENT_APPOINTMENT_TAG_PREFIX) &&
    !handlerParams.data.currentAppointment
  ) {
    return mergeTagName;
  }

  const handler = MERGE_TAGS_VALUE_HANDLERS[mergeTagName];

  if (handler) {
    return handler({ ...handlerParams, options }) ?? "";
  }
  return "";
};

// True when the frontend knows how to resolve this tag. A tag without a handler resolves to "" too,
// but that blank means "not ours to fill" (the backend resolves it at send time), not "no value".
export const hasMergeTagValueHandler = (name: string) =>
  Boolean(MERGE_TAGS_VALUE_HANDLERS[parseMergeTagOptions(name).mergeTagName]);

export const getFormattedDate = (value: string | null | undefined) => {
  if (!value) return undefined;

  return formatBackendDateOnly(value, "MM/dd/yyyy");
};

// The first letter of a middle name, as Anvil sends it ("ann marie" → "A"); blank without one.
export const getMiddleInitial = (middleName: string | null | undefined) =>
  middleName?.trim().charAt(0).toUpperCase() ?? "";

export const getPatientSignatureMergeTagOrPlaceholder = (
  patientSignatureURL?: string
) => {
  if (!patientSignatureURL) {
    return `<button class="sign-button" data-id="sign-button">Click to Add Signature</button>`;
  } else {
    return `<img class="patient-signature" src="${patientSignatureURL}" alt="signature" style="max-width:500px;max-height:130px" />`;
  }
};

export const getInvoiceMergeTagOrPlaceholderSelector = (
  mergeTagName: string
) => {
  return "." + mergeTagName.replaceAll(" ", "-").replaceAll(".", "-");
};

export const getSaleMergeTagOrPlaceholder = (
  content: string | undefined | null,
  mergeTagName: string,
  optionsString?: string
) => {
  const selector = getInvoiceMergeTagOrPlaceholderSelector(mergeTagName);

  if (content === undefined || content === null) {
    return `<button class="select-invoice-button ${selector.substring(1)}" data-id="select-invoice-button-${selector}" data-options="${optionsString || ""}">Click to Select Invoice</button>`;
  }
  return content ?? "";
};

export const getDateOrNull = (value: string | null | undefined) => {
  if (!value) return null;

  try {
    const date = new Date(value);
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date;
    }
  } catch {}
  return null;
};

export const replaceMergeTagInContent = (
  $: cheerio.Root,
  tagName: string,
  getContentFn: (options?: Record<string, any>) => string | undefined | null
) => {
  const result = $(getInvoiceMergeTagOrPlaceholderSelector(tagName));

  if (!result?.length) {
    return;
  }

  const optionsString = result.attr("data-options");

  result.replaceWith(
    getSaleMergeTagOrPlaceholder(
      getContentFn(
        optionsString
          ? parseMergeTagOptions(`{{${tagName}${optionsString}}}`).options
          : {}
      ),
      tagName,
      optionsString
    )
  );
};
